import {
    AccountMappingTemplateGroupItem,
    IAccountMappingTemplateGroup,
    IAnnualReport,
    IFinStatements,
    IItemNameType,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';

export class SortingItemsHelper {
    // Adding sorted Items arrays in template saved in annual report
    // Attention, this accesses finStatements directly, it's allowed here
    public static prepareSortedItemsInAnnualReport(annualReport: IAnnualReport): IAnnualReport {
        if (annualReport.finStatements != null && annualReport.finStatements.length === 2) {
            annualReport.finStatements.forEach((statement) => {
                if (statement.templateAndAccountsMapping.template == null) {
                    return;
                }
                statement.templateAndAccountsMapping.template.group =
                    SortingItemsHelper.mapGroupSortedItemsToSortedMappingItem(
                        statement.templateAndAccountsMapping.template.group,
                    );
            });
        }
        if (annualReport.fcp != null && annualReport.fcp.inputFinStatements.length === 2) {
            annualReport.fcp.inputFinStatements.forEach((statement) => {
                if (statement.templateAndAccountsMapping.template == null) {
                    return;
                }
                statement.templateAndAccountsMapping.template.group =
                    SortingItemsHelper.mapGroupSortedItemsToSortedMappingItem(
                        statement.templateAndAccountsMapping.template.group,
                    );
            });
        }
        if (annualReport.fcp != null && annualReport.fcp.outputFinStatements.length === 2) {
            annualReport.fcp.outputFinStatements.forEach((statement) => {
                if (statement.templateAndAccountsMapping.template == null) {
                    return;
                }
                statement.templateAndAccountsMapping.template.group =
                    SortingItemsHelper.mapGroupSortedItemsToSortedMappingItem(
                        statement.templateAndAccountsMapping.template.group,
                    );
            });
        }
        return annualReport;
    }

    public static mapGroupSortedItemsToSortedMappingItem(
        group: IAccountMappingTemplateGroup,
    ): IAccountMappingTemplateGroup {
        const sortedItems = group.sortedItems;
        if (sortedItems == null) {
            group.sortedItems = [];
            group.groups.forEach((groupOfGroups) => {
                (group.sortedItems as SortedMappingItem[]).push(groupOfGroups);
            });
            group.items.forEach((itemOfGroup) => {
                (group.sortedItems as SortedMappingItem[]).push(itemOfGroup);
            });
            group.groups.forEach((grp) => {
                SortingItemsHelper.mapGroupSortedItemsToSortedMappingItem(grp);
            });
        } else {
            let itemNameTypeToSortedMappingItemMap = new Map<string, SortedMappingItem>();
            itemNameTypeToSortedMappingItemMap = SortingItemsHelper.getItemNameTypeToSortedMappingItemMap(
                group,
                itemNameTypeToSortedMappingItemMap,
            );

            // REV-21327: rehydrate defensively instead of throwing. `sortedItems` stores each child as
            // a {name, type} reference; renaming, removing or re-typing a child (e.g. via grouping &
            // masking) can leave a reference that no longer matches any current child. Previously the
            // unmatched lookup threw "sorted item name type does not exist" inside the reducer, which
            // broke the entire report load. Instead: keep the entries that still resolve (preserving the
            // saved order), silently drop dangling ones, and append any current child that no valid entry
            // referenced so nothing disappears from the group.
            const rehydratedSortedItems: SortedMappingItem[] = [];
            const usedKeys = new Set<string>();

            sortedItems.forEach((sortedItemNameType) => {
                const type = sortedItemNameType.type != null ? sortedItemNameType.type : ItemType.GROUP;
                const sortedItemKey = SortingItemsHelper.createKeyWithNameAndType(sortedItemNameType.name, type);
                const sortedMappingItem = itemNameTypeToSortedMappingItemMap.get(sortedItemKey);
                if (sortedMappingItem != null && !usedKeys.has(sortedItemKey)) {
                    rehydratedSortedItems.push(sortedMappingItem);
                    usedKeys.add(sortedItemKey);
                }
            });

            group.groups.forEach((subGroup) => {
                const subGroupKey = SortingItemsHelper.createKeyWithNameAndType(subGroup.name, ItemType.GROUP);
                if (!usedKeys.has(subGroupKey)) {
                    rehydratedSortedItems.push(subGroup);
                    usedKeys.add(subGroupKey);
                }
            });
            group.items.forEach((item) => {
                const itemKey = SortingItemsHelper.createKeyWithNameAndType(item.name, item.type);
                if (!usedKeys.has(itemKey)) {
                    rehydratedSortedItems.push(item);
                    usedKeys.add(itemKey);
                }
            });

            group.sortedItems = rehydratedSortedItems;

            group.groups.forEach((group) => {
                this.mapGroupSortedItemsToSortedMappingItem(group);
            });
        }
        return group;
    }

    //Preparing a map with sortedItems (NameType) to sortedItems (full object)
    private static getItemNameTypeToSortedMappingItemMap(
        group: IAccountMappingTemplateGroup,
        itemNameTypeToSortedMappingItemMap: Map<string, SortedMappingItem>,
    ): Map<string, SortedMappingItem> {
        const groupSortedItemKey = SortingItemsHelper.createKeyWithNameAndType(group.name, ItemType.GROUP);
        itemNameTypeToSortedMappingItemMap.set(groupSortedItemKey, group);
        group.groups.forEach((group) => {
            SortingItemsHelper.getItemNameTypeToSortedMappingItemMap(group, itemNameTypeToSortedMappingItemMap);
        });
        group.items.forEach((item) => {
            const itemSortedItemKey = SortingItemsHelper.createKeyWithNameAndType(item.name, item.type);
            itemNameTypeToSortedMappingItemMap.set(itemSortedItemKey, item);
        });
        return itemNameTypeToSortedMappingItemMap;
    }

    private static createKeyWithNameAndType(name: string, type: ItemType): string {
        return `${name}-${type}`;
    }

    // Replacing sortedItems full object arrays with sortedItems NameType arrays
    public static prepareReportForSaving(annualReport: IAnnualReport): IAnnualReport {
        if (annualReport == null) {
            return annualReport;
        }
        if (annualReport.finStatements != null && annualReport.finStatements.length === 2) {
            annualReport.finStatements.forEach((statement) => {
                if (statement.templateAndAccountsMapping.template == null) {
                    return;
                }
                SortingItemsHelper.mapGroupSortedItemsToItemNameType(
                    statement.templateAndAccountsMapping.template.group,
                );
            });
        }
        return annualReport;
    }
    // Replacing sortedItems full object arrays with sortedItems NameType arrays
    public static prepareFinStatementsForSaving(finStatements: IFinStatements[]): IFinStatements[] {
        if (finStatements != null && finStatements.length === 2) {
            finStatements.forEach((statement) => {
                if (statement.templateAndAccountsMapping.template == null) {
                    return;
                }
                SortingItemsHelper.mapGroupSortedItemsToItemNameType(
                    statement.templateAndAccountsMapping.template.group,
                );
            });
        }
        return finStatements;
    }

    public static mapGroupSortedItemsToItemNameType(group: IAccountMappingTemplateGroup): IAccountMappingTemplateGroup {
        const sortedItems = group.sortedItems;
        if (sortedItems == null) {
            const groupsItemNameType: IItemNameType[] = [];
            group.groups.forEach((innerGroup) => {
                groupsItemNameType.push({
                    name: innerGroup.name,
                    type: ItemType.GROUP,
                } as IItemNameType);
            });
            group.items.forEach((item) => {
                groupsItemNameType.push({
                    name: item.name,
                    type: item.type,
                } as IItemNameType);
            });
            group.sortedItems = groupsItemNameType;
            group.groups.forEach((grp) => {
                SortingItemsHelper.mapGroupSortedItemsToItemNameType(grp);
            });
        } else {
            const sortedMappingItems = sortedItems.map((sortedItem) => {
                return SortingItemsHelper.mapSortedMappingItemToItemNameType(sortedItem);
            });

            group.sortedItems = sortedMappingItems;

            group.groups.forEach((group) => {
                SortingItemsHelper.mapGroupSortedItemsToItemNameType(group);
            });
        }
        return group;
    }

    private static mapSortedMappingItemToItemNameType(sortedItem: SortedMappingItem): IItemNameType {
        let sortedMappingItemNameType: IItemNameType;
        if (sortedItem.hasOwnProperty('groups')) {
            sortedMappingItemNameType = {
                name: sortedItem.name,
                type: ItemType.GROUP,
            };
        } else {
            sortedMappingItemNameType = {
                name: sortedItem.name,
                type: (sortedItem as AccountMappingTemplateGroupItem).type,
            };
        }

        return sortedMappingItemNameType;
    }
}
